/**
 * iter693 — Extracted from PrintersSettingsPage.jsx.
 *
 * Right-side Sheet drawer for adding or editing a printer. Three
 * grouped sections (Identity / Network / Behaviour) match the parent
 * page's Foodics-style organisation.
 *
 * State is OWNED by the parent — this component is a thin presentation
 * layer that takes 7 props:
 *   • open / onOpenChange  — drawer visibility
 *   • editing              — null when adding, printer object when editing
 *   • form / setForm       — the controlled form state
 *   • branches             — list of branches for the dropdown
 *   • saving               — disables the Save button while a request is in flight
 *   • onSave               — fires the API call (parent owns the network code)
 */
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Printer, Loader2, Smartphone, Radar } from 'lucide-react';
import { CONNECTION_METHODS, detectDevice } from '@/lib/platformDetect';
import { useState } from 'react';
import { listNativePrinters, requestNativePermissions, isNativeThermalAvailable } from '@/lib/nativeThermalPrint';
import { toast } from 'sonner';

export default function PrinterEditDrawer({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  branches,
  saving,
  onSave,
  onTestPrint,
  testing,
}) {
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [netScanning, setNetScanning] = useState(false);
  const [netResults, setNetResults] = useState([]);

  const scanNetwork = async () => {
    const { discoverNetworkPrinters } = await import('@/lib/nativeThermalPrint');
    if (!isNativeThermalAvailable()) {
      toast.info('Open this screen inside the Smarto Android app (on the same Wi-Fi) to auto-discover network printers.');
      return;
    }
    setNetScanning(true);
    try {
      const r = await discoverNetworkPrinters(form.port || 9100);
      setNetResults(r.printers || []);
      if (!r.printers || r.printers.length === 0) toast.info('No printers found on this Wi-Fi. Check they are powered on and on the same network.');
      else toast.success(`Found ${r.printers.length} printer(s) on ${r.subnet || 'Wi-Fi'}`);
    } catch { toast.error('Network scan failed'); }
    finally { setNetScanning(false); }
  };

  const scanDevices = async () => {
    const type = form.connection_type === 'usb' ? 'usb' : 'bluetooth';
    if (!isNativeThermalAvailable()) {
      toast.info('Open this screen inside the Smarto Android app to scan for Bluetooth/USB printers.');
      return;
    }
    setScanning(true);
    try {
      await requestNativePermissions(type);
      const list = await listNativePrinters(type);
      setScanResults(list || []);
      if (!list || list.length === 0) toast.info('No printers found. Pair the printer in Android settings first.');
    } catch {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="printer-form-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Printer size={18} className="text-orange-500" />
            {editing ? 'Edit printer' : 'Add new printer'}
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            {editing ? 'Update the printer configuration. Changes apply immediately.' : 'Configure a new thermal printer for this branch.'}
          </p>
        </SheetHeader>

        <div className="space-y-6 py-4">
          {/* Section 1 — Identity */}
          <section className="space-y-3" data-testid="form-section-identity">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Identity</h3>
            <div>
              <Label className="text-xs">Printer name *</Label>
              <Input
                placeholder="e.g., Main branch cashier"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 h-9 rounded-lg"
                data-testid="form-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="form-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receipt">Cashier / Receipt</SelectItem>
                  <SelectItem value="kitchen">Kitchen (KOT)</SelectItem>
                  <SelectItem value="bar">Bar (Drinks)</SelectItem>
                  <SelectItem value="label">Label / Sticker</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.type === 'kitchen' || form.type === 'bar') && (
              <div>
                <Label className="text-xs">Station <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  placeholder={form.type === 'kitchen' ? 'e.g., Cold prep, Hot grill, Pizza' : 'e.g., Main bar, Cocktails'}
                  value={form.station}
                  onChange={(e) => setForm({ ...form, station: e.target.value })}
                  className="mt-1 h-9 rounded-lg"
                  data-testid="form-station-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Use stations when one branch has multiple lanes of the same role.</p>
              </div>
            )}
            <div>
              <Label className="text-xs">Branch</Label>
              <Select
                value={form.branch_id || '__all__'}
                onValueChange={(v) => setForm({ ...form, branch_id: v === '__all__' ? null : v })}
              >
                <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="form-branch-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All branches (shared)</SelectItem>
                  {branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Section 2 — Connection / Method */}
          <section className="space-y-3" data-testid="form-section-connection">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connection method</h3>
            {(() => { const dev = detectDevice(); return (
              <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 p-2" data-testid="device-hint">
                <Smartphone size={14} className="text-sky-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-sky-800">This device: <strong>{dev.label}</strong>. {dev.note}</p>
              </div>
            ); })()}
            <div>
              <Label className="text-xs">How does Smarto reach this printer?</Label>
              <Select
                value={form.connection_type || 'network'}
                onValueChange={(v) => setForm({ ...form, connection_type: v, airprint_enabled: v === 'airprint' ? true : form.airprint_enabled })}
              >
                <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="form-connection-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONNECTION_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      <span>{m.label}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">· {m.platforms}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.connection_type === 'sunmi' && (
                <p className="text-[10px] text-amber-600 mt-1.5" data-testid="connection-sunmi-note">
                  Uses the <strong>Sunmi device&apos;s built-in printer</strong> — no pairing or address needed. Works only on Sunmi hardware inside the Smarto Android app.
                </p>
              )}
              {(form.connection_type === 'bluetooth' || form.connection_type === 'usb') && (
                <div className="mt-2 space-y-2" data-testid="connection-native-note">
                  <p className="text-[10px] text-amber-600">
                    {form.connection_type === 'bluetooth' ? 'Bluetooth' : 'USB'} printing runs through the <strong>Smarto Android app</strong> or the <strong>Smarto Desktop app</strong>. In a plain browser tab this printer falls back to network/AirPrint if configured.
                  </p>
                  <Label className="text-xs">Device address {form.connection_type === 'bluetooth' ? '(Bluetooth MAC)' : '(USB device id — Android)'}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.device_address || ''}
                      onChange={(e) => setForm({ ...form, device_address: e.target.value })}
                      placeholder={form.connection_type === 'bluetooth' ? 'e.g. 66:32:1B:00:12:34' : 'auto-detect or device id'}
                      className="h-9 rounded-lg text-xs"
                      data-testid="form-device-address"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={scanDevices} disabled={scanning} data-testid="form-scan-devices-btn">
                      {scanning ? <Loader2 size={14} className="animate-spin" /> : 'Scan'}
                    </Button>
                  </div>
                  {scanResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-32 overflow-auto" data-testid="scan-results">
                      {scanResults.map((d, i) => (
                        <button type="button" key={i} className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/50"
                          onClick={() => {
                            const patch = { ...form, device_address: d.address || d.deviceId || d.id };
                            // iter843 — USB scan results carry
                            // vendor_id/product_id; auto-fill both fields.
                            if (d.vendor_id != null) patch.usb_vendor_id = String(d.vendor_id);
                            if (d.product_id != null) patch.usb_product_id = String(d.product_id);
                            setForm(patch);
                            setScanResults([]);
                          }}
                          data-testid={`scan-result-${i}`}>
                          <strong>{d.deviceName || d.name || 'Printer'}</strong> · {d.address || d.deviceId || d.id}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* iter843 — Desktop-app USB Vendor / Product IDs.
                      Shown only when USB is picked. Users can either
                      type them (VID/PID printed on Epson/Star datasheets)
                      or hit "Detect from Desktop" which enumerates via
                      the Electron IPC bridge. */}
                  {form.connection_type === 'usb' && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50/50 dark:bg-sky-950/20 p-2 space-y-2" data-testid="usb-vid-pid-panel">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-semibold text-sky-800 dark:text-sky-300 uppercase tracking-wide">
                          Desktop USB (Windows / Mac)
                        </div>
                        <Button
                          type="button" size="sm" variant="outline"
                          className="h-6 text-[10px] px-2"
                          onClick={async () => {
                            try {
                              const { isElectronDesktop, electronListUsbPrinters } = await import('@/lib/electronDesktop');
                              if (!isElectronDesktop()) {
                                toast.info('Open the Smarto Desktop app first — this only works inside the native window.');
                                return;
                              }
                              const r = await electronListUsbPrinters();
                              if (r.ok && r.devices?.length) {
                                setScanResults(r.devices.map((d) => ({
                                  deviceName: d.product || d.manufacturer || 'USB Printer',
                                  address: `VID ${d.vendor_id} · PID ${d.product_id}`,
                                  vendor_id: d.vendor_id, product_id: d.product_id,
                                })));
                                toast.success(`Found ${r.devices.length} USB device${r.devices.length === 1 ? '' : 's'} — tap one to fill in.`);
                              } else {
                                toast.error(r.error || 'No USB devices found');
                              }
                            } catch (e) { toast.error(e.message); }
                          }}
                          data-testid="usb-detect-desktop-btn"
                        >
                          <Radar size={11} className="mr-1" /> Detect from Desktop
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Vendor ID (VID)</Label>
                          <Input
                            type="number"
                            value={form.usb_vendor_id ?? ''}
                            onChange={(e) => setForm({ ...form, usb_vendor_id: e.target.value })}
                            placeholder="e.g. 1208"
                            className="h-9 rounded-lg text-xs"
                            data-testid="form-usb-vendor-id"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Product ID (PID)</Label>
                          <Input
                            type="number"
                            value={form.usb_product_id ?? ''}
                            onChange={(e) => setForm({ ...form, usb_product_id: e.target.value })}
                            placeholder="e.g. 514"
                            className="h-9 rounded-lg text-xs"
                            data-testid="form-usb-product-id"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-sky-800/80 dark:text-sky-300/70">
                        Epson TM-T88VI: VID <code className="font-mono">1208</code>, PID <code className="font-mono">514</code>. Star TSP143: VID <code className="font-mono">1305</code>. Both must be numbers (decimal, not hex).
                      </p>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground">Tip: open this screen inside the Android app and tap <strong>Scan</strong> to pick your paired printer automatically.</p>
                </div>
              )}
            </div>
            {/* One-tap test print — validates the selected transport end-to-end */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full gap-2"
              disabled={!editing || testing === editing?.id}
              onClick={() => editing && onTestPrint?.(editing)}
              data-testid="form-test-print-btn"
            >
              {testing === editing?.id
                ? <><Loader2 size={14} className="animate-spin" /> Sending test…</>
                : <><Printer size={14} /> Send test print</>}
            </Button>
            {!editing && (
              <p className="text-[10px] text-muted-foreground -mt-1">Save the printer first, then send a test print to verify this connection.</p>
            )}
          </section>

          {/* Section 3 — Network */}
          {['network', 'airprint', 'windows'].includes(form.connection_type || 'network') && (
          <section className="space-y-3" data-testid="form-section-network">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Network</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">IP address</Label>
                <Input
                  placeholder="192.168.1.100"
                  value={form.ip_address}
                  onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                  className="mt-1 h-9 rounded-lg font-mono"
                  data-testid="form-ip-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Find it via the printer&apos;s self-test page (hold FEED on power-on).</p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={scanNetwork} disabled={netScanning} data-testid="form-scan-network-btn">
                  {netScanning ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                  {netScanning ? 'Scanning Wi-Fi…' : 'Auto-discover on Wi-Fi'}
                </Button>
                {netResults.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-32 overflow-auto mt-2" data-testid="net-scan-results">
                    {netResults.map((d, i) => (
                      <button type="button" key={i} className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/50"
                        onClick={() => { setForm({ ...form, ip_address: d.address, port: d.port || 9100 }); setNetResults([]); }}
                        data-testid={`net-result-${i}`}>
                        🖨️ {d.address}:{d.port || 9100}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Port</Label>
                <Input
                  type="number"
                  value={form.port}
                  onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 9100 })}
                  className="mt-1 h-9 rounded-lg"
                  data-testid="form-port-input"
                />
              </div>
              <div>
                <Label className="text-xs">Paper width</Label>
                <Select value={form.paper_width} onValueChange={(v) => setForm({ ...form, paper_width: v })}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="form-paper-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="58mm">58 mm</SelectItem>
                    <SelectItem value="80mm">80 mm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* iter678 — ePOS-Print Device ID (Epson printers only).
                  Many TM-m30II/m30III units ship with the printer's IP
                  address (e.g. "192.168.1.237") set as the ePOS Device
                  ID in WebConfig → ePOS-Print, instead of the default
                  "local_printer". When the values don't match, the
                  printer accepts the HTTP request but silently refuses
                  to print. Letting the operator type whatever value
                  the printer is using avoids editing WebConfig at all.
              */}
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  ePOS-Print Device ID
                  <span className="text-[10px] font-normal text-muted-foreground">(Epson only — leave blank if unsure)</span>
                </Label>
                <Input
                  placeholder={form.ip_address || 'local_printer'}
                  value={form.epos_device_id}
                  onChange={(e) => setForm({ ...form, epos_device_id: e.target.value })}
                  className="mt-1 h-9 rounded-lg font-mono"
                  data-testid="form-epos-device-id-input"
                />
                {/* iter679 — Quick-fill chips. Skip the typing — most
                    Epson printers ship with one of these two values
                    as the Device ID, so we offer both as one-tap
                    shortcuts. Tap to fill, then Save. */}
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <span className="text-[10px] text-muted-foreground">Quick-fill:</span>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, epos_device_id: 'local_printer' })}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-[10px] font-mono border border-emerald-300"
                    data-testid="devid-chip-local-printer"
                  >
                    local_printer
                  </button>
                  {form.ip_address && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, epos_device_id: form.ip_address })}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 text-[10px] font-mono border border-sky-300"
                      data-testid="devid-chip-use-ip"
                    >
                      {form.ip_address}
                    </button>
                  )}
                  {form.epos_device_id && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, epos_device_id: '' })}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-[10px] border border-zinc-300"
                      data-testid="devid-chip-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  On the printer&apos;s WebConfig (<code>https://{form.ip_address || '<printer-ip>'}/</code> →
                  <strong> Configuration → ePOS-Print</strong>), copy the <strong>Device ID</strong> value into this box.
                  The two chips above cover &gt;99% of Epson TM printers out of the box.
                </p>
              </div>
            </div>
          </section>
          )}

          {/* Section 3 — Behaviour */}
          <section className="space-y-3" data-testid="form-section-behaviour">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Behaviour</h3>
            <div>
              <Label className="text-xs">Print mode</Label>
              <Select value={form.print_mode} onValueChange={(v) => setForm({ ...form, print_mode: v })}>
                <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="form-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto — bridge first, OS dialog fallback</SelectItem>
                  <SelectItem value="silent">Silent — bridge only (no dialog)</SelectItem>
                  <SelectItem value="dialog">Dialog — always show OS print dialog</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Copies per ticket</Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.copies}
                onChange={(e) => setForm({ ...form, copies: parseInt(e.target.value) || 1 })}
                className="mt-1 h-9 rounded-lg w-24"
                data-testid="form-copies-input"
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <p className="text-xs font-medium flex items-center gap-1">
                  <span className="text-[14px]">🍎</span> AirPrint-capable
                </p>
                <p className="text-[10px] text-muted-foreground">Enable if this printer supports AirPrint (Epson TM-m30III, T88VI-iHub, or behind a Lantronix xPrintServer). iPads will print <strong>directly</strong> via Safari&apos;s print dialog — NO Print Bridge needed.</p>
              </div>
              <Switch
                checked={form.airprint_enabled}
                onCheckedChange={(v) => setForm({ ...form, airprint_enabled: v })}
                data-testid="form-airprint-switch"
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <p className="text-xs font-medium">Default for this role + branch</p>
                <p className="text-[10px] text-muted-foreground">Used when an order doesn&apos;t specify a printer.</p>
              </div>
              <Switch
                checked={form.is_default}
                onCheckedChange={(v) => setForm({ ...form, is_default: v })}
                data-testid="form-default-switch"
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <p className="text-xs font-medium">Auto-print on checkout</p>
                <p className="text-[10px] text-muted-foreground">Fire the receipt automatically when payment is taken.</p>
              </div>
              <Switch
                checked={form.auto_print}
                onCheckedChange={(v) => setForm({ ...form, auto_print: v })}
                data-testid="form-autoprint-switch"
              />
            </div>
          </section>
        </div>

        <SheetFooter className="border-t pt-3">
          <div className="flex w-full items-center justify-between">
            <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => onOpenChange(false)} data-testid="form-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
              disabled={saving || !form.name.trim()}
              onClick={onSave}
              data-testid="form-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              {editing ? 'Update printer' : 'Add printer'}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
